import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Download, Lock, Mail, Inbox, Briefcase } from "lucide-react";
import { API } from "@/lib/site";

const inputCls =
  "w-full bg-[#0b1e4b]/40 border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#ff6a00] transition-colors";
const labelCls = "block text-[11px] font-mono uppercase text-slate-300 mb-1.5";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="text-xs">
      <span className="font-mono uppercase text-slate-500 mr-2">{label}</span>
      <span className="text-slate-200 whitespace-pre-wrap break-words">{value}</span>
    </div>
  );
}

function RequestCard({ item, kind, token }) {
  const [downloading, setDownloading] = useState(false);
  const attachment = item.attachment;

  const download = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/admin/files/${attachment.stored}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = attachment.filename || "allegato";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download non riuscito", { description: "Il file potrebbe non essere più disponibile." });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-2" data-testid={`admin-${kind}-card`}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-bold text-white">{item.nome}</span>
        <span className="text-[10px] font-mono text-slate-500">{formatDate(item.created_at)}</span>
      </div>
      <Row label="Telefono" value={item.telefono} />
      <Row label="Email" value={item.email} />
      {kind === "contact" && <Row label="Motivo" value={item.motivo} />}
      {kind === "careers" && <Row label="Città" value={item.citta} />}
      {kind === "careers" && <Row label="Esperienza" value={item.esperienza} />}
      <Row label="Messaggio" value={item.messaggio || item.presentazione} />
      {attachment && (
        <button
          type="button"
          onClick={download}
          disabled={downloading}
          data-testid={`admin-${kind}-download`}
          className="inline-flex items-center gap-2 text-xs font-mono text-[#ff6a00] hover:underline disabled:opacity-50"
        >
          {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
          {attachment.filename}
        </button>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("sn_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("contacts");

  const load = useCallback(
    async (tk) => {
      try {
        const res = await axios.get(`${API}/admin/requests`, { headers: { Authorization: `Bearer ${tk}` } });
        setData(res.data);
      } catch (err) {
        if (err?.response?.status === 401) {
          localStorage.removeItem("sn_admin_token");
          setToken("");
          setData(null);
        } else {
          toast.error("Errore di caricamento", { description: "Riprova tra poco." });
        }
      }
    },
    []
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem("sn_admin_token", res.data.token);
      setToken(res.data.token);
      toast.success("Accesso effettuato");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Accesso non riuscito", { description: typeof detail === "string" ? detail : "Riprova." });
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("sn_admin_token");
    setToken("");
    setData(null);
  };

  return (
    <div className="min-h-screen text-slate-100 relative" data-testid="admin-page">
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden="true">
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #10307a 0%, #16296b 30%, #241c63 58%, #10173a 100%)" }}></div>
      </div>
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-10">
          <a href="/" data-testid="admin-back-home">
            <img src="/logo-supernova.webp" alt="Supernova Energy Network" className="h-10 w-auto" />
          </a>
          {token && (
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-slate-300 hover:text-white px-3 py-1.5 rounded border border-white/15 hover:border-white/40 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Esci
            </button>
          )}
        </div>

        {!token ? (
          <div className="max-w-md mx-auto rounded-3xl border border-white/15 bg-white/[0.03] backdrop-blur-md p-8 sm:p-10" data-testid="admin-login-panel">
            <div className="w-14 h-14 mx-auto mb-6 rounded-2xl bg-white/5 border border-white/20 flex items-center justify-center">
              <Lock className="w-7 h-7 text-[#ff6a00]" />
            </div>
            <h1 className="font-display text-2xl uppercase text-white text-center mb-1">Pannello Amministratore</h1>
            <p className="text-xs text-slate-400 text-center mb-8">Area riservata — richieste e candidature dal sito</p>
            <form onSubmit={login} className="space-y-4" data-testid="admin-login-form">
              <div>
                <label htmlFor="admin-email" className={labelCls}>Email</label>
                <input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email amministratore" data-testid="admin-email-input" className={inputCls} />
              </div>
              <div>
                <label htmlFor="admin-password" className={labelCls}>Password</label>
                <input id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="admin-password-input" className={inputCls} />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-login-button"
                className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#ff6a00] to-[#f43f5e] hover:brightness-110 disabled:opacity-60 text-white font-bold text-xs uppercase tracking-widest py-3.5 rounded-lg transition shadow-lg shadow-[#ff6a00]/25"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                {loading ? "Accesso in corso..." : "Accedi"}
              </button>
            </form>
          </div>
        ) : (
          <div data-testid="admin-dashboard">
            <h1 className="font-display text-3xl uppercase text-white mb-6">Richieste ricevute</h1>
            <div className="flex gap-3 mb-6">
              <button
                type="button"
                onClick={() => setTab("contacts")}
                data-testid="admin-tab-contacts"
                className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider px-4 py-2.5 rounded-lg border transition-colors ${tab === "contacts" ? "bg-[#ff6a00]/15 border-[#ff6a00]/50 text-[#ff6a00]" : "border-white/15 text-slate-400 hover:text-white"}`}
              >
                <Inbox className="w-4 h-4" /> Contattaci ({data?.contacts?.length ?? 0})
              </button>
              <button
                type="button"
                onClick={() => setTab("careers")}
                data-testid="admin-tab-careers"
                className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider px-4 py-2.5 rounded-lg border transition-colors ${tab === "careers" ? "bg-purple-500/15 border-purple-400/50 text-purple-300" : "border-white/15 text-slate-400 hover:text-white"}`}
              >
                <Briefcase className="w-4 h-4" /> Lavora con noi ({data?.applications?.length ?? 0})
              </button>
            </div>
            {!data ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm" data-testid="admin-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
              </div>
            ) : (
              <div className="space-y-4">
                {(tab === "contacts" ? data.contacts : data.applications).map((item) => (
                  <RequestCard key={item.id} item={item} kind={tab === "contacts" ? "contact" : "careers"} token={token} />
                ))}
                {(tab === "contacts" ? data.contacts : data.applications).length === 0 && (
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-10 text-center" data-testid="admin-empty-state">
                    <Mail className="w-8 h-8 text-slate-600 mx-auto mb-3" />
                    <p className="text-sm text-slate-400">Nessuna richiesta presente per questa sezione.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
